import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import ChevronRightIcon from '@/assets/icons/chevronRightNew';
import LightningBoltIcon from '@/assets/icons/lightning-bolt';
import WalletCashIcon from '@/assets/icons/wallet-cash';
import WalletGoldIcon from '@/assets/icons/wallet-gold';
import { Text } from '@/components/TextComponent';
import { AnimatedPressableOpacity } from '@/components/animated-pressable-opacity/AnimatedPressableOpacity';
import { SafetyShadow } from '@/components/entry-card/SafetyShadow';
import { Box, Column, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { withOpacity } from '@/styles/styles';
import { toCoinsCurrency, toLocaleCurrency, toSocialCurrency } from '@/utils/numeric/currency';

import { CoinPackagesType } from '../hooks/use-coin-packages';
import { PackageReward } from '../utils/coin-package-rewards';
import { PackageRewardBadge } from './PackageRewardBadge';

const IMAGE_SIZE = 32;
const SAFETY_SHADOW_WIDTH = 24;
const BANNER_HEIGHT = 70;
const BANNER_OVERLAP = -38;
const DASHED_DIVIDER_COLOR = withOpacity(ds.colorValue.background.neutral.inverse, 0.05);

type Props = {
    item: CoinPackagesType;
    rewards: PackageReward[];
    onPress: (item: CoinPackagesType) => void;
    isMostPopular?: boolean;
    showFirstTimeBoost?: boolean;
};

export const CoinPackageCard = ({
    item,
    rewards,
    onPress,
    isMostPopular = false,
    showFirstTimeBoost = false,
}: Props) => {
    const { t } = useTranslation(['common', 'sweeps']);

    const hasCash = item.cash_amount > 0;

    return (
        <AnimatedPressableOpacity
            onPress={() => onPress(item)}
            activeOpacity={0.8}
            testID="coin-package-card"
            accessibilityRole="button"
        >
            {isMostPopular ? (
                <Box
                    backgroundColor={ds.colorToken.background.brand.strong}
                    borderTopLeftRadius="r20"
                    borderTopRightRadius="r20"
                    paddingTop="s6"
                    alignItems="center"
                    style={styles.banner}
                >
                    <Text variant="labelLarge2">{t('sweeps:most_popular')}</Text>
                </Box>
            ) : null}
            <Box
                backgroundColor={ds.colorToken.background.neutral.subtler}
                borderRadius="r20"
                borderWidth={1}
                borderColor={isMostPopular ? ds.colorToken.background.brand.strong : ds.colorToken.border.subtle}
                paddingVertical="s16"
            >
                <Row paddingHorizontal="s16" gap="s12" alignItems="center">
                    <Box
                        width={IMAGE_SIZE + 16}
                        height={IMAGE_SIZE + 16}
                        padding="s8"
                        borderRadius="r12"
                        backgroundColor={ds.colorToken.background.neutral.subtle}
                    >
                        {item.image_url ? (
                            <Image source={{ uri: item.image_url }} resizeMode="contain" style={styles.image} />
                        ) : (
                            <WalletGoldIcon size={IMAGE_SIZE} />
                        )}
                        {showFirstTimeBoost ? (
                            <Box
                                position="absolute"
                                top={-6}
                                left={-6}
                                width={20}
                                height={20}
                                borderRadius="r6"
                                padding="s2"
                                backgroundColor={ds.colorToken.background.accent.amber.strong}
                            >
                                <LightningBoltIcon color={ds.colorValue.icon.inverse} />
                            </Box>
                        ) : null}
                    </Box>
                    <Column flex={1}>
                        <Row gap="s16" height={24} alignItems="center">
                            <Row flex={1} gap="s4" alignItems="center" overflow="hidden">
                                <Text variant="bodyMedium2" fontWeight="500" lineHeight={20} numberOfLines={1}>
                                    {toCoinsCurrency(item.coin_amount || 0)}
                                </Text>
                                <Text
                                    variant="bodyMedium2"
                                    fontWeight="500"
                                    lineHeight={20}
                                    color="neutral300"
                                    numberOfLines={1}
                                >
                                    {t('sweeps:gold_coins')}
                                </Text>
                                <SafetyShadow width={SAFETY_SHADOW_WIDTH} style={styles.safetyShadow} />
                            </Row>
                            <Row gap="s4" alignItems="center" flexShrink={0}>
                                <Text variant="titleMedium2" lineHeight={20}>
                                    {toLocaleCurrency(item.real_money_price)}
                                </Text>
                                <ChevronRightIcon />
                            </Row>
                        </Row>
                        {hasCash ? (
                            <Row gap="s4" height={24} alignItems="center">
                                <WalletCashIcon size={16} />
                                <Text variant="labelLarge2" color={ds.colorToken.text.success}>
                                    +{toSocialCurrency(item.cash_amount)} {t('common:betr_cash')}
                                </Text>
                            </Row>
                        ) : null}
                    </Column>
                </Row>
                {rewards.length > 0 ? (
                    <>
                        <Box marginTop="s16" marginHorizontal="s16" height={1} overflow="hidden">
                            <Box height={2} borderWidth={1} borderRadius="r2" style={styles.dashedDivider} />
                        </Box>
                        <Column marginTop="s12" paddingHorizontal="s16" gap="s8">
                            <Text variant="bodySmall2" color="neutral300">
                                {t('sweeps:additional_rewards')}
                            </Text>
                            <Row flexWrap="wrap" gap="s8">
                                {rewards.map(reward => (
                                    <PackageRewardBadge key={reward.id} reward={reward} />
                                ))}
                            </Row>
                        </Column>
                    </>
                ) : null}
            </Box>
        </AnimatedPressableOpacity>
    );
};

const styles = StyleSheet.create({
    banner: {
        height: BANNER_HEIGHT,
        marginBottom: BANNER_OVERLAP,
    },
    image: {
        width: IMAGE_SIZE,
        height: IMAGE_SIZE,
    },
    safetyShadow: {
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: SAFETY_SHADOW_WIDTH,
    },
    dashedDivider: {
        borderStyle: 'dashed',
        borderColor: DASHED_DIVIDER_COLOR,
    },
});
